import React, { useEffect, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AddIcon from '@mui/icons-material/Add'
import ReorderIcon from '@mui/icons-material/Reorder'
import {
  accordionSummaryStyles, AlertDialog,
  cardStyles,
  EditActions,
  IconButtonWithTooltip,
  type PersonAddress, type PersonPhone,
  UsAddressFields, usAddressIsValid, useGlobalMessageHandler,
  DragAndDropStack
} from 'components'
import { type BaseAddress, type Building } from 'services'
import { filterBuildingsForMemberByChamber, getEmptyPersonAddress, getEmptyPersonPhone } from 'helpers'
import {
  updatePersonAddress,
  createPersonAddress,
  deletePersonAddress
} from 'services/person/us/edit/personAddressService'
import { formatErrorFieldMessages } from 'interfaces'
import DeleteIcon from '@mui/icons-material/Delete'
import { PhoneCard } from './phone-card'
import { type LegislativeRoles } from 'services/person/us/edit/legislativeRolesService'
import { US_LEGISLATIVE_MEMBER_ROLES, US_STAFFER_ROLES, US_ELECT_ROLES, US_CANDIDATE_ROLES, PHONE_TYPES } from 'utils'

export interface PersonAddressesPhonesCardProps {
  personId: number
  originalAddresses: PersonAddress[]
  loading: boolean
  onAddressesUpdate: React.Dispatch<React.SetStateAction<PersonAddress[]>>
  refreshAddresses: (updatedAddressId?: number) => Promise<number | undefined>
  roles: LegislativeRoles | null
}

export const PersonAddressesPhonesCard: React.FC<PersonAddressesPhonesCardProps> = ({
  originalAddresses,
  loading,
  onAddressesUpdate,
  personId,
  refreshAddresses,
  roles
}) => {
  const [currentAddresses, setCurrentAddresses] = React.useState<PersonAddress[]>(originalAddresses)
  const [expanded, setExpanded] = React.useState(true)
  const [selectedTab, setSelectedTab] = React.useState(0)
  const [editingTab, setEditingTab] = React.useState<number>(-1)
  const [loadingClick, setLoadingClick] = useState(false)
  const [addressToDelete, setAddressToDelete] = useState<PersonAddress | null>(null)
  const { showSuccessMessage, showErrorMessage, clearMessages } = useGlobalMessageHandler()
  const [memberRoleId, setMemberRoleId] = useState<number | null>(null)
  const [addressTypes, setAddressTypes] = useState<Record<number, any>>({})
  const [addressTypesLutData, setAddressTypesLutData] = useState<Record<number, any>>({})
  const [savedTabLabels, setSavedTabLabels] = useState<string[]>([])
  const [editingAddressId, setEditingAddressId] = useState<number | null>(null)
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false)
  const [selectedAddressIndex, setSelectedAddressIndex] = useState<number>(-1)

  useEffect(() => {
    setCurrentAddresses(originalAddresses)
    setSavedTabLabels(generateTabLabels(originalAddresses))

    if (editingAddressId !== null) {
      const newIndex = originalAddresses.findIndex(addr => addr.id === editingAddressId)
      if (newIndex !== -1) {
        setSelectedTab(newIndex)
      }
      setEditingAddressId(null)
    }
  }, [originalAddresses, editingAddressId])

  useEffect(() => {
    if (roles === null || roles === undefined) return

    const ALLOWED_ROLE_IDS = new Set(Object.values(US_LEGISLATIVE_MEMBER_ROLES))

    const memberRole = Array.from(roles.member_roles.values()).find(({ legrole }) =>
      ALLOWED_ROLE_IDS.has(legrole.role.id)
    )

    if (memberRole !== undefined) {
      setMemberRoleId(memberRole.legrole.role.id)
    }
  }, [roles])

  useEffect(() => {
    const types = getAddressTypesByRole()
    setAddressTypes(types)
  }, [memberRoleId])

  useEffect(() => {
    const storedValue = localStorage.getItem('roleAddressTypeLutValues')
    if (storedValue != null) {
      try {
        const parsedData = JSON.parse(storedValue).address_types
        setAddressTypesLutData(parsedData)
      } catch (error) {
        showErrorMessage('Error parsing address types')
      }
    }
  }, [])

  const activeMemberRole = roles?.member_roles.find(({ legrole }) =>
    Object.values(US_LEGISLATIVE_MEMBER_ROLES).includes(legrole.role.id)
  )
  const memberBuildingFilter = activeMemberRole != null
    ? (buildings: Building[]) => filterBuildingsForMemberByChamber(
        buildings,
        activeMemberRole.legchamber?.id,
        activeMemberRole.state?.abbrev
      )
    : undefined

  const handleAddressChange = (baseAddress: BaseAddress, index: number): void => {
    setCurrentAddresses(prevAddresses => {
      const newAddresses = [...prevAddresses]
      newAddresses[index] = {
        ...prevAddresses[index],
        ...baseAddress,
        personId: prevAddresses[index].personId,
        typeId: prevAddresses[index].typeId,
        phones: prevAddresses[index].phones
      }
      return newAddresses
    })
  }

  const handleAddressPhoneChange = (address: PersonAddress, index: number): void => {
    setCurrentAddresses(prevAddresses => {
      const newAddresses = [...prevAddresses]
      newAddresses[index] = address
      return newAddresses
    })
  }
  const isCreating = currentAddresses.some(address => address.id === 0)

  const handleUpdateAddressType = async (address: PersonAddress): Promise<void> => {
    try {
      const response = await updatePersonAddress(address.id, address)

      if (response.result == null) {
        return
      }

      showSuccessMessage('Address and type updated successfully')

      const newIndex = await refreshAddresses(address.id)
      if (newIndex !== undefined) {
        setSelectedTab(newIndex)
      }

      setEditingTab(-1)
    } catch (error) {
      showErrorMessage(formatErrorFieldMessages(
        (error as any).response?.data?.errors ?? { error: 'Failed to update address type' }
      ))
    } finally {
      setLoadingClick(false)
    }
  }

  const handlePostClick = async (address: PersonAddress): Promise<void> => {
    clearMessages()
    if (!usAddressIsValid(address, showErrorMessage)) return
    setLoadingClick(true)

    const originalAddress = originalAddresses.find(a => a.id === address.id)

    if (address.id !== 0 && originalAddress !== undefined && originalAddress.typeId !== address.typeId) {
      await handleUpdateAddressType(address)
      return
    }

    try {
      const value = await (address.id === 0
        ? createPersonAddress(address.personId, address)
        : updatePersonAddress(address.id, address))

      showSuccessMessage(`This Person address has been successfully ${address.id === 0 ? 'created' : 'updated'}!\n`)

      let addressIdToFind = address.id
      if (address.id === 0 && value.result?.id != null && value.result.id > 0) {
        addressIdToFind = value.result.id
        setEditingAddressId(value.result.id)
      }

      const newIndex = await refreshAddresses(addressIdToFind)
      if (newIndex !== undefined) {
        setSelectedTab(newIndex)
      }

      setEditingTab(-1)
    } catch (error) {
      showErrorMessage(formatErrorFieldMessages(
        (error as any).response?.data?.errors ?? { error: 'Failed to update address' }
      ))
    } finally {
      setLoadingClick(false)
    }
  }

  const handleDeleteClick = async (address: PersonAddress | null): Promise<void> => {
    if (address === null) return

    if (address.id !== 0) {
      clearMessages()
      setLoadingClick(true)

      try {
        const value = await deletePersonAddress(address.id)

        if (!value.succeeded) {
          showErrorMessage(formatErrorFieldMessages(value.errors))
          return
        }

        showSuccessMessage('This Person address has been successfully deleted')

        await refreshAddresses()

        setAddressToDelete(null)
        setSelectedTab(0)
        setEditingTab(-1)
      } catch (error) {
        showErrorMessage('Failed to delete Person Address')
      } finally {
        setLoadingClick(false)
      }
    } else {
      setCurrentAddresses(prevAddresses => prevAddresses.filter(addr => addr !== address))
      onAddressesUpdate(prevAddresses => prevAddresses.filter(addr => addr !== address))
      setAddressToDelete(null)
      setSelectedTab(0)
      setEditingTab(-1)
      showSuccessMessage('This Person address has been successfully deleted')
    }
  }

  const handleAddressReset = (index: number): void => {
    const updatedAddresses = [...currentAddresses]
    updatedAddresses[index] = originalAddresses[index]
    setCurrentAddresses(updatedAddresses)
    onAddressesUpdate(updatedAddresses)
  }

  const handleAddAddress = (): void => {
    const newAddress = getEmptyPersonAddress(personId)
    setCurrentAddresses(prevAddresses => [...prevAddresses, newAddress])
    onAddressesUpdate(prevAddresses => [...prevAddresses, newAddress])
    setSelectedTab(currentAddresses.length)
    setEditingTab(currentAddresses.length)
    setEditingAddressId(newAddress.id)
  }

  const handleAccordionChange = (): void => {
    setExpanded(!expanded)
  }

  const getAddressTypeDescription = (typeDesc: string): string => {
    if (memberRoleId == null) return typeDesc

    const prefixes: Record<number, string> = {
      [US_LEGISLATIVE_MEMBER_ROLES.FED_LEGISLATIVE_MEMBER]: 'Member ',
      [US_LEGISLATIVE_MEMBER_ROLES.STATE_LEGISLATIVE_MEMBER]: 'Member ',
      [US_ELECT_ROLES.FED_LEGISLATOR_ELECT]: 'Elect ',
      [US_ELECT_ROLES.STATE_LEGISLATOR_ELECT]: 'Elect ',
      [US_CANDIDATE_ROLES.FED_LEGISLATOR_CANDIDATE]: 'Candidate ',
      [US_CANDIDATE_ROLES.STATE_LEGISLATOR_CANDIDATE]: 'Candidate '
    }

    const prefix = prefixes[memberRoleId]
    return prefix != null ? `${prefix}${typeDesc}` : typeDesc
  }

  const getPhoneTypeDescription = (typeId: number | undefined): string => {
    if (typeId === undefined) return 'Unknown'

    const phoneTypeMap: Record<number, string> = {
      [PHONE_TYPES.PHONE]: 'Phone',
      [PHONE_TYPES.FAX]: 'Fax',
      [PHONE_TYPES.TTY_TDD]: 'TTY/TDD',
      [PHONE_TYPES.GENERAL_INFORMATION]: 'General Information',
      [PHONE_TYPES.RECORDER_INFORMATION]: 'Recorder Information'
    }

    return phoneTypeMap[typeId] ?? 'Unknown'
  }

  const getAddressTypesByRole = (): Record<number, any> => {
    if (Object.keys(addressTypesLutData).length === 0 || memberRoleId == null) return {}

    const staffRoles: Record<number, number[]> = {
      [US_LEGISLATIVE_MEMBER_ROLES.STATE_LEGISLATIVE_MEMBER]: [US_STAFFER_ROLES.STATE_MEMBER_STAFF, US_STAFFER_ROLES.STATE_COMMITTE_STAFF],
      [US_LEGISLATIVE_MEMBER_ROLES.FED_LEGISLATIVE_MEMBER]: [US_STAFFER_ROLES.FEDERAL_MEMBER_STAFF, US_STAFFER_ROLES.FEDERAL_COMMITTE_STAFF]
    }

    const allowedRoles = [memberRoleId, ...(staffRoles[memberRoleId] ?? [])]

    return Object.fromEntries(
      Object.entries(addressTypesLutData).filter(([, addressType]) =>
        addressType.role?.id != null && allowedRoles.includes(addressType.role.id)
      )
    )
  }

  const getNextDisplayOrderForType = (addresses: PersonAddress[], typeId: number): number => {
    const addressesOfSameType = addresses.filter(addr => addr.typeId === typeId)

    if (addressesOfSameType.length === 0) {
      return 1
    }

    const maxDisplayOrder = Math.max(
      ...addressesOfSameType.map(addr => addr.displayOrder ?? 0)
    )

    return maxDisplayOrder + 1
  }

  const handleAddressTypeChange = (addressIndex: number, newTypeId: number): void => {
    if (addressIndex < 0 || newTypeId <= 0) {
      console.error('Invalid parameters for address type change')
      return
    }

    setCurrentAddresses(prevAddresses => {
      if (addressIndex >= prevAddresses.length) {
        console.error('Address index out of bounds')
        return prevAddresses
      }

      const currentAddress = prevAddresses[addressIndex]

      if (currentAddress.typeId === newTypeId) {
        return prevAddresses
      }

      const newAddresses = [...prevAddresses]
      const nextDisplayOrder = getNextDisplayOrderForType(prevAddresses, newTypeId)

      newAddresses[addressIndex] = {
        ...currentAddress,
        typeId: newTypeId,
        displayOrder: nextDisplayOrder
      }

      return newAddresses
    })
  }

  const generateTabLabels = (addresses: PersonAddress[]): string[] => {
    const counters: Record<string, number> = {}
    return addresses.map(address => {
      if (address.id === 0) return 'New Address'

      const typeLabel = address.addressType?.name != null && address.addressType.name !== ''
        ? getAddressTypeDescription(address.addressType.name)
        : 'No Type Available'

      if (counters[typeLabel] === undefined) counters[typeLabel] = 1
      else counters[typeLabel]++

      return `${typeLabel} #${address.displayOrder ?? counters[typeLabel]}`
    })
  }

  const getTabLabels = (): string[] => {
    return savedTabLabels.length > 0 ? savedTabLabels : generateTabLabels(currentAddresses)
  }

  const getAddressTypeSelectValue = (address: PersonAddress): string | number => {
    return Object.entries(addressTypes).length > 0 ? address.typeId : 'disabled'
  }

  const renderAddressTypeMenuItems = (): JSX.Element[] => {
    return Object.entries(addressTypes).map(([id, type]) => {
      return (
        <MenuItem key={id} value={Number(type.id)}>
          {type.type_desc}
        </MenuItem>
      )
    })
  }

  const handleOpenReorderDialog = (addressIndex: number): void => {
    setSelectedAddressIndex(addressIndex)
    setReorderDialogOpen(true)
  }

  const handleCloseReorderDialog = (): void => {
    setReorderDialogOpen(false)
    setSelectedAddressIndex(-1)
  }

  const handleSavePhoneOrder = async (): Promise<void> => {
    if (selectedAddressIndex === -1) return

    const address = currentAddresses[selectedAddressIndex]
    if (address === undefined || address === null) return

    clearMessages()
    setLoadingClick(true)

    try {
      const response = await updatePersonAddress(address.id, address)

      if (response.result == null) {
        showErrorMessage('Failed to update phone order')
        return
      }

      showSuccessMessage('Phone order updated successfully')
      await refreshAddresses(address.id)
    } catch (error) {
      showErrorMessage(formatErrorFieldMessages(
        (error as any).response?.data?.errors ?? { error: 'Failed to update phone order' }
      ))
    } finally {
      setLoadingClick(false)
      handleCloseReorderDialog()
    }
  }

  const updatePhoneDisplayOrder = async (phones: PersonPhone[], selectedPhone: PersonPhone): Promise<any> => {
    if (selectedAddressIndex === -1) return { succeeded: false }

    const address = currentAddresses[selectedAddressIndex]
    if (address === undefined || address === null) return { succeeded: false }

    const selectedPhoneType = selectedPhone.typeId ?? 0
    const otherPhones = address.phones.filter(p => (p.typeId ?? 0) !== selectedPhoneType)

    const reorderedPhones = phones.filter(p => (p.typeId ?? 0) === selectedPhoneType)

    const updatedPhonesOfSameType = reorderedPhones.map((phone, index) => ({
      ...phone,
      displayOrder: index + 1
    }))

    const allUpdatedPhones = [...otherPhones, ...updatedPhonesOfSameType]

    handleAddressPhoneChange({
      ...address,
      phones: allUpdatedPhones
    }, selectedAddressIndex)

    return { succeeded: true }
  }

  const renderPhoneCard = (phone: PersonPhone): React.ReactNode => {
    return (
      <Box sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Typography>
          {phone.phoneNumber}
          <Typography component="span" color="text.secondary" sx={{ ml: 1 }}>
            ({getPhoneTypeDescription(phone.typeId)})
          </Typography>
        </Typography>
      </Box>
    )
  }

  const hasUnsavedPhone = (phones: PersonPhone[]): boolean => {
    return phones.some(phone => phone.id === null || phone.id <= 0)
  }

  const getNextDisplayOrderForPhoneType = (phones: PersonPhone[], typeId: number): number => {
    const phonesOfSameType = phones.filter(phone => phone.typeId === typeId)

    if (phonesOfSameType.length === 0) {
      return 1
    }

    const maxDisplayOrder = Math.max(
      ...phonesOfSameType.map(phone => phone.displayOrder ?? 0)
    )

    return maxDisplayOrder + 1
  }

  return (
    <Card sx={cardStyles}>
      <Accordion expanded={expanded} onChange={handleAccordionChange}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummaryStyles}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Typography variant="h6">Addresses and Phones</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Tabs value={selectedTab} onChange={(_event, newValue) => { setSelectedTab(newValue) }} variant="scrollable" scrollButtons="auto">
            {getTabLabels().map((label, index) => (
              <Tab key={index} label={label} />
            ))}
          </Tabs>
          <CardContent>
            <Box>
            {currentAddresses.map((address, addressIndex) => (
              <Box key={address.id} hidden={selectedTab !== addressIndex}>
                  <UsAddressFields
                    showPhones = {false}
                    comAddress = {address}
                    disabled={loading || editingTab !== addressIndex}
                    startExpanded={true}
                    updateAddress={(baseAddress: BaseAddress) => { handleAddressChange(baseAddress, addressIndex) }}
                    resetAddress={() => { handleAddressReset(addressIndex) }}
                    filterBuildings={memberBuildingFilter}
                    showSecondaryBuildingButton={true}
                  />
                <Grid container spacing={2} sx={{ mt: 0.1 }}>
                  <Grid item xs={3}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel id={`address-type-select-label-${addressIndex}`}>Address Type</InputLabel>
                      <Select
                        labelId={`address-type-select-label-${addressIndex}`}
                        id={`address-type-select-${addressIndex}`}
                        value={ getAddressTypeSelectValue(address) }
                        onChange={(e) => { handleAddressTypeChange(addressIndex, Number(e.target.value)) }}
                        label="Address Type"
                        disabled={loading || editingTab !== addressIndex || Object.entries(addressTypes).length === 0}
                      >
                        {Object.entries(addressTypes).length > 0
                          ? renderAddressTypeMenuItems()
                          : (
                              <MenuItem disabled value='disabled'>No address types available</MenuItem>
                            )
                        }
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <Box pt={2}>
                  {address.phones.length > 0 && (
                    <>
                      <Typography variant="body2" color={'secondary.dark'} sx={{ fontWeight: 'bold' }} paddingBottom={2}>Phones:</Typography>
                      <Stack spacing={1}>
                        {address.phones
                          .sort((a, b) => {
                            const typeOrderA = a.typeId ?? 0
                            const typeOrderB = b.typeId ?? 0

                            if (typeOrderA !== typeOrderB) {
                              return typeOrderA - typeOrderB
                            }

                            const displayOrderA = a.displayOrder ?? 0
                            const displayOrderB = b.displayOrder ?? 0
                            return displayOrderA - displayOrderB
                          })
                          .reduce((acc: JSX.Element[], phone, index, array) => {
                            acc.push(
                              <PhoneCard<PersonPhone>
                                key={phone.id}
                                phone={phone}
                                disabled={loading || editingTab !== addressIndex}
                                onDeletePhone={(phoneToDelete) => {
                                  handleAddressPhoneChange({
                                    ...address,
                                    phones: address.phones.filter(p => p.id !== phoneToDelete.id)
                                  }, addressIndex)
                                }}
                                onUpdatePhone={(updatedPhone) => {
                                  handleAddressPhoneChange({
                                    ...address,
                                    phones: address.phones.map(p => p.id === updatedPhone.id ? updatedPhone : p)
                                  }, addressIndex)
                                }}
                                address={address}
                              />
                            )

                            if (index < array.length - 1 && array[index + 1].typeId !== phone.typeId) {
                              acc.push(
                                <Divider key={`divider-${phone.id ?? `temp-${index}`}`} sx={{ my: 1 }} />
                              )
                            }

                            return acc
                          }, [])}
                      </Stack>
                    </>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <IconButtonWithTooltip
                      label="Add Phone"
                      color="secondary"
                      disabled={loading || editingTab !== addressIndex || hasUnsavedPhone(address.phones)}
                      onClick={() => {
                        const newPhoneTypeId = PHONE_TYPES.PHONE
                        const nextDisplayOrder = getNextDisplayOrderForPhoneType(address.phones, newPhoneTypeId)
                        const newPhone = {
                          ...getEmptyPersonPhone(personId, address.id),
                          typeId: newPhoneTypeId,
                          displayOrder: nextDisplayOrder
                        }

                        handleAddressPhoneChange(
                          {
                            ...address,
                            phones: [...address.phones, newPhone]
                          },
                          addressIndex
                        )
                      }}
                      icon={<AddIcon />}
                    />
                    <IconButtonWithTooltip
                      label="Reorder Phones"
                      color="secondary"
                      disabled={loading || editingTab !== addressIndex || address.phones.length <= 1 || hasUnsavedPhone(address.phones)}
                      onClick={() => { handleOpenReorderDialog(addressIndex) }}
                      icon={<ReorderIcon />}
                    />
                  </Box>
                </Box>
                  <Box sx={{ pt: 3, display: 'flex', justifyContent: 'end' }}>
                    {loadingClick && <CircularProgress/>}
                    <EditActions
                      entityName="Address"
                      editing={editingTab === addressIndex}
                      disabled={loading}
                      saving={loadingClick}
                      onEdit={() => { setEditingTab(addressIndex); setEditingAddressId(address.id) }}
                      onCancel={() => {
                        handleAddressReset(addressIndex)
                        setEditingTab(-1)
                      }}
                      onSave={() => { void handlePostClick(address) }}
                    />
                    <IconButtonWithTooltip
                      label="Delete Address"
                      color="secondary"
                      onClick={() => { setAddressToDelete(address) }}
                      disabled={loading || loadingClick}
                      icon={<DeleteIcon />}
                    />
                  </Box>
              </Box>
            ))}
            </Box>
          </CardContent>
        </AccordionDetails>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mr: 2, mb: 2 }}>
          <IconButtonWithTooltip
            label="Add new address"
            color="primary"
            onClick={handleAddAddress}
            disabled={loading || isCreating}
            icon={<AddIcon />}
          />
        </Box>
      </Accordion>
      <AlertDialog
        onClose={() => { setAddressToDelete(null) }}
        open={addressToDelete !== null}
        onButtonClick={() => { void handleDeleteClick(addressToDelete) }}
        title='Person Address Deletion'
        contentText='This action cannot be undone.'
        nameButtonCancel='Close'
        nameButtonConfirm='Delete'
        containedButton = {true}
      />
      <Dialog
        open={reorderDialogOpen}
        onClose={handleCloseReorderDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reorder Phone Numbers</DialogTitle>
        <DialogContent>
          {selectedAddressIndex !== -1 && currentAddresses[selectedAddressIndex] !== undefined && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Drag and drop to reorder phone numbers within each type. You cannot reorder across different phone types.
              </Typography>

              {Object.entries(
                currentAddresses[selectedAddressIndex].phones.reduce<Record<number, PersonPhone[]>>((acc, phone) => {
                  const typeId = phone.typeId ?? 0
                  if (acc[typeId] === undefined || acc[typeId] === null) {
                    acc[typeId] = []
                  }
                  acc[typeId].push(phone)
                  return acc
                }, {})
              ).map(([typeId, phones], index, array) => (
                <React.Fragment key={`phone-type-${typeId}`}>
                  <Typography variant="subtitle2" sx={{ mt: index > 0 ? 2 : 0, mb: 1 }}>
                    {getPhoneTypeDescription(Number(typeId))}
                  </Typography>
                  <DragAndDropStack
                    id={`phone-reorder-${currentAddresses[selectedAddressIndex].id}-type-${typeId}`}
                    originalItems={phones}
                    itemRenderer={(item) => renderPhoneCard(item)}
                    updateDisplayOrder={updatePhoneDisplayOrder}
                    disableDrag={false}
                    makeGray={false}
                  />
                  {index < array.length - 1 && <Divider sx={{ my: 2 }} />}
                </React.Fragment>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReorderDialog}>Cancel</Button>
          <Button
            onClick={() => { void handleSavePhoneOrder() }}
            variant="contained"
            disabled={loadingClick}
          >
            {loadingClick ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}
